import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Paper,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { InfoDadosCadastraisResposta } from '.'

interface DialogDetalheDadosCadastraisProps {
    open: boolean
    onClose: () => void
    dados: InfoDadosCadastraisResposta
}

export default function DialogDetalheDadosCadastrais({
    open,
    onClose,
    dados
}: DialogDetalheDadosCadastraisProps) {
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR')
    }

    const isCNPJ = (documento: string | undefined) => {
        if (!documento) return false;
        const cleaned = documento.replace(/\D/g, '');
        return cleaned.length === 14;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Detalhes dos Dados Cadastrais</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3}>
                    {/* Dados Pessoais */}
                    <Paper elevation={2} sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                            {isCNPJ(dados?.documento) ? 'Dados da Empresa' : 'Dados Pessoais'}
                        </Typography>
                        <Grid container spacing={2} size={{ xs: 12 }}>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="body2"><b>{isCNPJ(dados?.documento)? 'Razão Social' : 'Nome Público'}:</b> {dados?.nome_publico}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="body2"><b>{isCNPJ(dados?.documento) ? 'Nome Fantasia' : 'Nome Social'}:</b> {dados?.nome_social}</Typography>
                            </Grid>
                            {!isCNPJ(dados?.documento) && dados?.rg && (
                                <Grid size={{ xs: 4 }}>
                                    <Typography variant="body2"><b>RG:</b> {dados?.rg}</Typography>
                                </Grid>
                            )}
                            <Grid size={{ xs: 4 }}>
                                <Typography variant="body2"><b>{isCNPJ(dados?.documento) ? 'CNPJ' : 'CPF'}:</b> {dados?.documento || dados?.cpf}</Typography>
                            </Grid>
                            {!isCNPJ(dados?.documento) && dados?.sexo && (
                                <Grid size={{ xs: 4 }}>
                                    <Typography variant="body2"><b>Sexo:</b> {dados?.sexo}</Typography>
                                </Grid>
                            )}
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="body2"><b>{isCNPJ(dados?.documento) ? 'Data de Fundação' : 'Data Nascimento'}:</b> {formatDate(dados?.data_nascimento)}</Typography>
                            </Grid>
                            {dados?.nacionalidade && (
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="body2"><b>Nacionalidade:</b> {dados?.nacionalidade}</Typography>
                                </Grid>
                            )}
                            {dados?.nome_mae && (
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="body2"><b>Nome da Mãe:</b> {dados?.nome_mae}</Typography>
                                </Grid>
                            )}
                            {dados?.nome_pai && (
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="body2"><b>Nome do Pai:</b> {dados?.nome_pai}</Typography>
                                </Grid>
                            )}
                            {dados?.data_adimissao && (
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="body2"><b>Data de Admissão:</b> {formatDate(dados?.data_adimissao)}</Typography>
                                </Grid>
                            )}
                            {dados?.registro_estadual_produtor && (
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="body2"><b>Registro Estadual Produtor:</b> {dados?.registro_estadual_produtor}</Typography>
                                </Grid>
                            )}
                            {dados?.inscricao_estadual && (
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="body2"><b>Inscrição Estadual:</b> {dados?.inscricao_estadual}</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>

                    {/* Endereço */}
                    <Paper elevation={2} sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                            Endereço
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 3 }}>
                                <Typography variant="body2"><b>CEP:</b> {dados?.cep}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="body2"><b>Endereço:</b> {dados?.endereco}</Typography>
                            </Grid>
                            <Grid size={{ xs: 3 }}>
                                <Typography variant="body2"><b>Número:</b> {dados?.numero || 'S/N'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                <Typography variant="body2"><b>Complemento:</b> {dados?.complemento}</Typography>
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                <Typography variant="body2"><b>Bairro:</b> {dados?.bairro}</Typography>
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                <Typography variant="body2"><b>Cidade/UF:</b> {dados?.cidade}/{dados?.uf}</Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Dados do Cônjuge */}
                    {dados?.possui_conjuge === 'Sim' && (
                        <Paper elevation={2} sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom color="primary">
                                Dados do Cônjuge
                            </Typography>
                            <Grid container spacing={2}>
                                {dados?.regime_casamento && (
                                    <Grid size={{ xs: 6 }}>
                                        <Typography variant="body2"><b>Regime de Casamento:</b> {dados.regime_casamento}</Typography>
                                    </Grid>
                                )}
                                {dados?.tipo_relacionamento && (
                                    <Grid size={{ xs: 6 }}>
                                        <Typography variant="body2"><b>Tipo de Relacionamento:</b> {dados.tipo_relacionamento}</Typography>
                                    </Grid>
                                )}
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="body2"><b>Nome:</b> {dados?.nome_conjuge}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="body2"><b>Nome Social:</b> {dados?.nome_social_conjuge}</Typography>
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                    <Typography variant="body2"><b>RG:</b> {dados?.rg_conjuge}</Typography>
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                    <Typography variant="body2"><b>CPF:</b> {dados?.cpf_conjuge}</Typography>
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                    <Typography variant="body2"><b>Sexo:</b> {dados?.sexo_conjuge}</Typography>
                                </Grid>
                                {dados?.telefone_conjuge && (
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><b>Telefone:</b> {dados.telefone_conjuge}</Typography>
                                    </Grid>
                                )}
                                {dados?.nome_mae_conjuge && (
                                    <Grid size={{ xs: 6 }}>
                                        <Typography variant="body2"><b>Nome da Mãe:</b> {dados.nome_mae_conjuge}</Typography>
                                    </Grid>
                                )}
                                {dados?.nome_pai_conjuge && (
                                    <Grid size={{ xs: 6 }}>
                                        <Typography variant="body2"><b>Nome do Pai:</b> {dados.nome_pai_conjuge}</Typography>
                                    </Grid>
                                )}
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="body2"><b>Data Nascimento:</b> {formatDate(dados?.data_nascimento_conjuge ?? '-')}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="body2"><b>Nacionalidade:</b> {dados?.nacionalidade_conjuge}</Typography>
                                </Grid>
                                {dados?.cep_conjuge && (
                                    <>
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="subtitle2" color="primary"><b>Endereço</b></Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3 }}>
                                            <Typography variant="body2"><b>CEP:</b> {dados.cep_conjuge}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography variant="body2"><b>Endereço:</b> {dados.endereco_conjuge}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3 }}>
                                            <Typography variant="body2"><b>Número:</b> {dados.numero_conjuge || 'S/N'}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 4 }}>
                                            <Typography variant="body2"><b>Bairro:</b> {dados.bairro_conjuge}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 4 }}>
                                            <Typography variant="body2"><b>Cidade:</b> {dados.cidade_conjuge}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 4 }}>
                                            <Typography variant="body2"><b>UF:</b> {dados.uf_conjuge}</Typography>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Paper>
                    )}

                    {/* Dados Financeiros */}
                    <Paper elevation={2} sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                            Dados Financeiros
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 4 }}>
                                <Typography variant="body2"><b>Alíquota INSS:</b> {dados?.aliquota_inss}%</Typography>
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                <Typography variant="body2"><b>Alíquota SENAR:</b> {dados?.aliquota_senar}%</Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Accordions para seções expandíveis */}
                    <Stack spacing={1}>
                        {/* Fazendas */}
                        {dados?.fazendas?.length > 0 && (
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">Fazendas ({dados.fazendas.length})</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={2}>
                                        {dados.fazendas.map((fazenda: any, index: number) => (
                                            <Paper key={index} elevation={1} sx={{ p: 2 }}>
                                                <Grid container spacing={2}>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="body2"><b>Nome:</b> {fazenda.nome}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="body2"><b>CNPJ:</b> {fazenda.cnpj}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 4 }}>
                                                        <Typography variant="body2"><b>Produtor de Leite:</b> {fazenda.produtor_leite}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 4 }}>
                                                        <Typography variant="body2"><b>Produtor de Café:</b> {fazenda.produtor_cafe}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 4 }}>
                                                        <Typography variant="body2"><b>Produtor de Milho:</b> {fazenda.produtor_milho}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {/* Sociedades */}
                        {dados?.possui_sociedade === 'Sim' && dados?.sociedades && dados?.sociedades?.length > 0 && (
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">Sociedades ({dados.sociedades.length})</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={2}>
                                        {dados.sociedades.map((sociedade: any, index: number) => (
                                            <Paper key={index} elevation={1} sx={{ p: 2 }}>
                                                <Stack spacing={2}>
                                                    <Typography variant="subtitle1" color="primary"><b>Dados do Sócio</b></Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid size={{ xs: 4 }}>
                                                            <Typography variant="body2"><b>Tipo:</b> {sociedade.tipo_socio === 'F' ? 'Física' : 'Jurídica'}</Typography>
                                                        </Grid>
                                                        <Grid size={{ xs: 4 }}>
                                                            <Typography variant="body2"><b>Nome:</b> {sociedade.nome}</Typography>
                                                        </Grid>
                                                        <Grid size={{ xs: 4 }}>
                                                            <Typography variant="body2"><b>Nome Social:</b> {sociedade.nome_social}</Typography>
                                                        </Grid>
                                                        {sociedade.tipo_socio === 'F' ? (
                                                            <>
                                                                <Grid size={{ xs: 3 }}>
                                                                    <Typography variant="body2"><b>RG:</b> {sociedade.rg}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 3 }}>
                                                                    <Typography variant="body2"><b>CPF:</b> {sociedade.cpf}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 3 }}>
                                                                    <Typography variant="body2"><b>Sexo:</b> {sociedade.sexo}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 3 }}>
                                                                    <Typography variant="body2"><b>Telefone:</b> {sociedade.telefone || '-'}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 4 }}>
                                                                    <Typography variant="body2"><b>Data Nascimento:</b> {formatDate(sociedade.data_nascimento)}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 4 }}>
                                                                    <Typography variant="body2"><b>Nacionalidade:</b> {sociedade.nacionalidade}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 4 }}>
                                                                    <Typography variant="body2"><b>Grau Parentesco:</b> {sociedade.grau_parentesco || '-'}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 6 }}>
                                                                    <Typography variant="body2"><b>Nome da Mãe:</b> {sociedade.nome_mae || '-'}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 6 }}>
                                                                    <Typography variant="body2"><b>Nome do Pai:</b> {sociedade.nome_pai || '-'}</Typography>
                                                                </Grid>
                                                            </>
                                                        ) : (
                                                            <Grid size={{ xs: 12 }}>
                                                                <Typography variant="body2"><b>CNPJ:</b> {sociedade.cnpj}</Typography>
                                                            </Grid>
                                                        )}
                                                        {sociedade.cep && (
                                                            <>
                                                                <Grid size={{ xs: 12 }}>
                                                                    <Typography variant="subtitle2" color="primary"><b>Endereço</b></Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 3 }}>
                                                                    <Typography variant="body2"><b>CEP:</b> {sociedade.cep}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 6 }}>
                                                                    <Typography variant="body2"><b>Endereço:</b> {sociedade.endereco}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 3 }}>
                                                                    <Typography variant="body2"><b>Número:</b> {sociedade.numero || 'S/N'}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 4 }}>
                                                                    <Typography variant="body2"><b>Bairro:</b> {sociedade.bairro}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 4 }}>
                                                                    <Typography variant="body2"><b>Cidade:</b> {sociedade.cidade}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 4 }}>
                                                                    <Typography variant="body2"><b>UF:</b> {sociedade.uf}</Typography>
                                                                </Grid>
                                                            </>
                                                        )}
                                                    </Grid>
                                                    {sociedade.possui_conjuge === 'Sim' && (
                                                        <>
                                                            <Typography variant="subtitle1" color="primary"><b>Dados do Cônjuge do Sócio</b></Typography>
                                                            <Grid container spacing={2}>
                                                                <Grid size={{ xs: 6 }}>
                                                                    <Typography variant="body2"><b>Nome:</b> {sociedade.nome_conjuge}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 6 }}>
                                                                    <Typography variant="body2"><b>Nome Social:</b> {sociedade.nome_social_conjuge}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 3 }}>
                                                                    <Typography variant="body2"><b>RG:</b> {sociedade.rg_conjuge}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 3 }}>
                                                                    <Typography variant="body2"><b>CPF:</b> {sociedade.cpf_conjuge}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 3 }}>
                                                                    <Typography variant="body2"><b>Sexo:</b> {sociedade.sexo_conjuge}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 3 }}>
                                                                    <Typography variant="body2"><b>Data Nascimento:</b> {formatDate(sociedade.data_nascimento_conjuge)}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 6 }}>
                                                                    <Typography variant="body2"><b>Nome da Mãe:</b> {sociedade.nome_mae_conjuge || '-'}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 6 }}>
                                                                    <Typography variant="body2"><b>Nome do Pai:</b> {sociedade.nome_pai_conjuge || '-'}</Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 12 }}>
                                                                    <Typography variant="body2"><b>Nacionalidade:</b> {sociedade.nacionalidade_conjuge}</Typography>
                                                                </Grid>
                                                                {sociedade.cep_conjuge && (
                                                                    <>
                                                                        <Grid size={{ xs: 12 }}>
                                                                            <Typography variant="subtitle2" color="primary"><b>Endereço do Cônjuge</b></Typography>
                                                                        </Grid>
                                                                        <Grid size={{ xs: 3 }}>
                                                                            <Typography variant="body2"><b>CEP:</b> {sociedade.cep_conjuge}</Typography>
                                                                        </Grid>
                                                                        <Grid size={{ xs: 6 }}>
                                                                            <Typography variant="body2"><b>Endereço:</b> {sociedade.endereco_conjuge}</Typography>
                                                                        </Grid>
                                                                        <Grid size={{ xs: 3 }}>
                                                                            <Typography variant="body2"><b>Número:</b> {sociedade.numero_conjuge || 'S/N'}</Typography>
                                                                        </Grid>
                                                                        <Grid size={{ xs: 4 }}>
                                                                            <Typography variant="body2"><b>Bairro:</b> {sociedade.bairro_conjuge}</Typography>
                                                                        </Grid>
                                                                        <Grid size={{ xs: 4 }}>
                                                                            <Typography variant="body2"><b>Cidade:</b> {sociedade.cidade_conjuge}</Typography>
                                                                        </Grid>
                                                                        <Grid size={{ xs: 4 }}>
                                                                            <Typography variant="body2"><b>UF:</b> {sociedade.uf_conjuge}</Typography>
                                                                        </Grid>
                                                                    </>
                                                                )}
                                                            </Grid>
                                                        </>
                                                    )}
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {/* Procuradores */}
                        {dados?.possui_procurador === 'Sim' && dados?.procuradores && dados?.procuradores.length > 0 && (
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">Procuradores ({dados.procuradores.length})</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={2}>
                                        {dados.procuradores.map((procurador: any, index: number) => (
                                            <Paper key={index} elevation={1} sx={{ p: 2 }}>
                                                <Grid container spacing={2}>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="body2"><b>Nome:</b> {procurador.nome}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="body2"><b>Nome Social:</b> {procurador.nome_social || '-'}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 4 }}>
                                                        <Typography variant="body2"><b>CPF:</b> {procurador.cpf}</Typography>
                                                    </Grid>
                                                    {procurador.rg && (
                                                        <Grid size={{ xs: 4 }}>
                                                            <Typography variant="body2"><b>RG:</b> {procurador.rg}</Typography>
                                                        </Grid>
                                                    )}
                                                    {procurador.telefone && (
                                                        <Grid size={{ xs: 4 }}>
                                                            <Typography variant="body2"><b>Telefone:</b> {procurador.telefone}</Typography>
                                                        </Grid>
                                                    )}
                                                    {procurador.sexo && (
                                                        <Grid size={{ xs: 4 }}>
                                                            <Typography variant="body2"><b>Sexo:</b> {procurador.sexo}</Typography>
                                                        </Grid>
                                                    )}
                                                    {procurador.data_nascimento && (
                                                        <Grid size={{ xs: 6 }}>
                                                            <Typography variant="body2"><b>Data Nascimento:</b> {formatDate(procurador.data_nascimento)}</Typography>
                                                        </Grid>
                                                    )}
                                                    {procurador.nacionalidade && (
                                                        <Grid size={{ xs: 6 }}>
                                                            <Typography variant="body2"><b>Nacionalidade:</b> {procurador.nacionalidade}</Typography>
                                                        </Grid>
                                                    )}
                                                    {procurador.nome_mae && (
                                                        <Grid size={{ xs: 6 }}>
                                                            <Typography variant="body2"><b>Nome da Mãe:</b> {procurador.nome_mae}</Typography>
                                                        </Grid>
                                                    )}
                                                    {procurador.nome_pai && (
                                                        <Grid size={{ xs: 6 }}>
                                                            <Typography variant="body2"><b>Nome do Pai:</b> {procurador.nome_pai}</Typography>
                                                        </Grid>
                                                    )}
                                                    {procurador.grau_parentesco && (
                                                        <Grid size={{ xs: 6 }}>
                                                            <Typography variant="body2"><b>Grau de Parentesco:</b> {procurador.grau_parentesco}</Typography>
                                                        </Grid>
                                                    )}
                                                    {procurador.cep && (
                                                        <>
                                                            <Grid size={{ xs: 12 }}>
                                                                <Typography variant="subtitle2" color="primary"><b>Endereço</b></Typography>
                                                            </Grid>
                                                            <Grid size={{ xs: 3 }}>
                                                                <Typography variant="body2"><b>CEP:</b> {procurador.cep}</Typography>
                                                            </Grid>
                                                            <Grid size={{ xs: 6 }}>
                                                                <Typography variant="body2"><b>Endereço:</b> {procurador.endereco}</Typography>
                                                            </Grid>
                                                            <Grid size={{ xs: 3 }}>
                                                                <Typography variant="body2"><b>Número:</b> {procurador.numero || 'S/N'}</Typography>
                                                            </Grid>
                                                            {procurador.complemento && (
                                                                <Grid size={{ xs: 4 }}>
                                                                    <Typography variant="body2"><b>Complemento:</b> {procurador.complemento}</Typography>
                                                                </Grid>
                                                            )}
                                                            <Grid size={{ xs: 4 }}>
                                                                <Typography variant="body2"><b>Bairro:</b> {procurador.bairro}</Typography>
                                                            </Grid>
                                                            <Grid size={{ xs: 4 }}>
                                                                <Typography variant="body2"><b>Cidade:</b> {procurador.cidade}</Typography>
                                                            </Grid>
                                                            <Grid size={{ xs: 4 }}>
                                                                <Typography variant="body2"><b>UF:</b> {procurador.uf}</Typography>
                                                            </Grid>
                                                        </>
                                                    )}
                                                </Grid>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {/* Terceiros */}
                        {dados?.possui_terceiros === 'Sim' && dados?.terceiros && dados?.terceiros.length > 0 && (
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6">Terceiros Autorizados a Comprar ({dados.terceiros.length})</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={2}>
                                        {dados.terceiros.map((terceiro: any, index: number) => (
                                            <Paper key={index} elevation={1} sx={{ p: 2 }}>
                                                <Grid container spacing={2}>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="body2"><b>Nome:</b> {terceiro.nome}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="body2"><b>CPF:</b> {terceiro.cpf}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 4 }}>
                                                        <Typography variant="body2"><b>Telefone:</b> {terceiro.telefone}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 4 }}>
                                                        <Typography variant="body2"><b>Função/Relação:</b> {terceiro.tipo_relacionamento}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 4 }}>
                                                        <Typography variant="body2"><b>Compra produtos com receita:</b> {terceiro.compra_prod_receita ? 'Sim' : 'Não'}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    )
}